import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  equalTo,
  get,
  orderByChild,
  push,
  query,
  ref,
  set,
  update,
} from "firebase/database";
import {
  LuArrowLeft,
  LuBuilding2,
  LuCheck,
  LuChevronRight,
  LuClock,
  LuExternalLink,
  LuFlag,
  LuLoaderCircle,
  LuMapPin,
  LuSend,
  LuShieldCheck,
} from "react-icons/lu";
import { auth, database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import AppPage, { State } from "../components/AppPage";
import Avatar from "../components/UserAvatar";
import ModalSheet from "../components/ModalSheet";
import ShareJobSheet from "../components/ShareJobSheet";
import HireCheckout from "../components/HireCheckout";
import { BankPayoutForm } from "./BankPayouts";
import { call, dateValue, rows, safeUrl, useRecord } from "../utils/appData";
import {
  creatorSplit,
  hasJobBank,
  jobBudget,
  jobDay,
  jobIsOpen,
  jobRand,
  jobRange,
  jobReferences,
} from "../utils/jobDetails";
import useExchangeRates from "../hooks/useExchangeRates";

const card =
  "rounded-2xl border border-black/5 bg-white p-4 shadow-sm dark:border-[#363e46] dark:bg-[#23272c]";
const primary =
  "flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[#327691] px-5 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50";
const secondary =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-bold dark:border-[#363e46] dark:bg-[#23272c]";
const muted = "text-sm text-slate-500 dark:text-slate-400";
const input =
  "mt-2 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:border-[#327691] focus:ring-2 focus:ring-[#327691]/20 dark:border-[#363e46] dark:bg-[#23272c]";
const reasons = [
  "Spam or scam",
  "Harassment or bullying",
  "Inappropriate or explicit content",
  "Impersonation",
  "Other",
];
function ago(value) {
  const ms = dateValue(value);
  if (!ms) return "Recently posted";
  const hours = Math.max(0, Math.floor((Date.now() - ms) / 3600000));
  return hours < 1
    ? "Just posted"
    : hours < 24
      ? hours + "h ago"
      : Math.floor(hours / 24) + "d ago";
}
function PayBreakdown({ amount }) {
  if (!amount) return null;
  const { fee, payout } = creatorSplit(amount);
  return (
    <div className="mb-4 rounded-xl border border-black/5 bg-[#fafafb] p-3 text-sm dark:border-white/10 dark:bg-[#2c3036]">
      <div className="flex justify-between gap-4">
        <span className={muted}>Job budget</span>
        <b>{jobRand(amount)}</b>
      </div>
      <div className="mt-2 flex justify-between gap-4">
        <span className={muted}>zylen fee (7%)</span>
        <b>{jobRand(fee)}</b>
      </div>
      <p className="mt-1 text-[11px] leading-4 text-slate-500 dark:text-slate-400">
        Paid through the app. Once the hirer approves your work, payout is
        within 2 business days.
      </p>
      <div className="mt-3 flex justify-between gap-4 border-t border-black/5 pt-3 font-bold dark:border-white/10">
        <span>You receive</span>
        <span className="text-emerald-600 dark:text-emerald-400">
          {jobRand(payout)}
        </span>
      </div>
    </div>
  );
}
export default function JobDetails() {
  const { jobId } = useParams();
  return <JobView key={jobId} jobId={jobId} />;
}
function JobView({ jobId }) {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const uid = currentUser?.uid;
  const job = useRecord(jobId ? `Jobs/${jobId}` : null);
  const j = job.data;
  const owner = Boolean(uid && j?.hostId === uid);
  const apps = useRecord(
    j && uid
      ? owner
        ? `JobApplications/${jobId}`
        : `JobApplications/${jobId}/${uid}`
      : null,
  );
  const user = useRecord(uid ? `Users/${uid}` : null);
  const poster = useRecord(j?.hostId ? `Users/${j.hostId}` : null);
  const { exchangeRates } = useExchangeRates();
  const [postedCount, setPostedCount] = useState(null);
  const [applicantCount, setApplicantCount] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [pitch, setPitch] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [reason, setReason] = useState(reasons[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const applicantsRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (!j?.hostId) return;
    let active = true;
    get(query(ref(database, "Jobs"), orderByChild("hostId"), equalTo(j.hostId)))
      .then((s) => {
        if (active) setPostedCount(Object.keys(s.val() || {}).length);
      })
      .catch(() => { });
    // A denied aggregate must not block the applicant's own application.
    get(ref(database, `JobApplications/${jobId}`))
      .then((s) => {
        if (active) setApplicantCount(Object.keys(s.val() || {}).length);
      })
      .catch(() => { });
    return () => {
      active = false;
    };
  }, [j?.hostId, jobId]);
  const applied = submitted || (!owner && Boolean(apps.data));
  const open = jobIsOpen(j);
  const amount = jobBudget(j?.payRate, exchangeRates.ZAR);
  const company = j?.companyName || j?.hostName || poster.data?.name || "Hirer";
  const bankReady = hasJobBank(user.data || currentUser);
  const references = jobReferences(j);
  const dates = jobRange(j?.workStart, j?.workEnd);
  const deadline = jobDay(j?.deadline || j?.neededBy);
  const linkOk =
    !portfolio.trim() ||
    Boolean(safeUrl(portfolio.trim()) && !/\s/.test(portfolio.trim()));
  const checkUser = () => {
    if (!uid || auth.currentUser?.uid !== uid)
      throw new Error("Please sign in to continue.");
  };
  function startApply() {
    setError("");
    if (!uid) {
      setError("Please sign in to apply.");
      return;
    }
    if (!open || applied || owner || apps.loading || apps.error) return;
    if (!bankReady) {
      setBankOpen(true);
      return;
    }
    setApplyOpen(true);
  }
  async function apply(event) {
    event.preventDefault();
    if (
      lock.current ||
      !pitch.trim() ||
      !linkOk ||
      !open ||
      applied ||
      owner ||
      apps.loading ||
      apps.error
    )
      return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      checkUser();
      const result = await call("applyToJobWithBankCheck", {
        jobId,
        coverMessage: pitch.trim(),
        portfolioLink: portfolio.trim(),
      });
      if (!result?.alreadyApplied)
        setApplicantCount((count) => (count === null ? null : count + 1));
      setSubmitted(true);
      setApplyOpen(false);
      setNotice(
        "Application sent! The hirer can now view your profile, showreel and message.",
      );
    } catch (err) {
      if (err.details?.reason === "BANK_SETUP_REQUIRED") {
        setApplyOpen(false);
        setBankOpen(true);
      } else setError(err.message || "Your application could not be sent.");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function report(event) {
    event.preventDefault();
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      checkUser();
      const reportRef = push(ref(database, "Reports"));
      await set(reportRef, {
        id: reportRef.key,
        type: "job",
        targetId: jobId,
        reportedUserId: j.hostId || "",
        reportedBy: uid,
        reason,
        note: note.trim(),
        contentPreview: j.title || "",
        status: "open",
        createdAt: Date.now(),
      });
      setReportOpen(false);
      setNotice("Thanks for reporting. Our team will review this job.");
    } catch (err) {
      setError(err.message || "Could not send the report.");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function shortlist(applicant) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      checkUser();
      await update(ref(database, `JobApplications/${jobId}/${applicant.id}`), {
        status: "shortlisted",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <AppPage
      title="Job"
      hideMobileHeading
      headerActions={
        j && !owner ? (
          <button
            type="button"
            aria-label="Report job"
            onClick={() => {
              setError("");
              setReportOpen(true);
            }}
            className="grid h-11 w-11 place-items-center rounded-full text-slate-500 hover:bg-black/5 dark:text-slate-300"
          >
            <LuFlag size={19} />
          </button>
        ) : (
          <span />
        )
      }
    >
      <State loading={job.loading} error={job.error} empty={!j}>
        This job is no longer available.
      </State>
      {!job.loading && !job.error && j && (
        <div className="w-full max-w-[900px]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-5 hidden items-center gap-2 text-sm font-bold text-[#327691] dark:text-[#5FAFCB] md:inline-flex"
          >
            <LuArrowLeft /> Back
          </button>
          <div className="flex items-center gap-3">
            <Avatar
              user={{
                name: company,
                profilePic:
                  j.imageUrl || j.hostProfilePic || poster.data?.profilePic,
              }}
              className="h-[52px] w-[52px] shrink-0 rounded-[14px] object-cover"
            />
            <div className="min-w-0">
              <h2 className="break-words text-[21px] font-extrabold leading-7 tracking-tight">
                {j.title}
              </h2>
              <p className={muted + " mt-1 flex items-center gap-1.5"}>
                <LuBuilding2 className="shrink-0" size={14} />
                {company}
              </p>
            </div>
          </div>
          <div className="mb-5 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
            {j.location && (
              <>
                <LuMapPin size={13} />
                <span>{j.location}</span>
                <span>·</span>
              </>
            )}
            <LuClock size={13} />
            <span>{ago(j.createdAt)}</span>
            {j.role && (
              <>
                <span>·</span>
                <b>{j.role}</b>
              </>
            )}
          </div>
          <dl
            className={
              card + " mb-6 divide-y divide-black/5 !py-0 dark:divide-white/10"
            }
          >
            {[
              [
                "Budget",
                amount
                  ? jobRand(amount) + " / " + (j.rateUnit || "project")
                  : j.payRate || "To be agreed",
              ],
              ...(dates ? [["Shoot date/s", dates]] : []),
              ...(deadline ? [["Delivery deadline", deadline]] : []),
              ...(!dates && !deadline && j.datesFlexible
                ? [["Dates", "Flexible — to be agreed"]]
                : []),
              [
                "Applicants",
                owner
                  ? rows(apps.data).length + " so far"
                  : applicantCount === null
                    ? "—"
                    : applicantCount + " so far",
              ],
            ].map(([key, value]) => (
              <div
                key={key}
                className="flex items-center justify-between gap-4 py-3.5 text-sm"
              >
                <dt className="shrink-0 text-slate-500 dark:text-slate-400">
                  {key}
                </dt>
                <dd
                  className={
                    "break-words text-right font-semibold " +
                    (key === "Budget"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "")
                  }
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          {j.description?.trim() && (
            <section className="mb-6">
              <h3 className="mb-3 text-sm font-bold">About this job</h3>
              <p className="whitespace-pre-wrap break-words text-sm leading-6">
                {j.description}
              </p>
            </section>
          )}
          {references.length > 0 && (
            <section className="mb-6">
              <h3 className="mb-3 text-sm font-bold">
                Reference the hirer sent
              </h3>
              <div className="space-y-3">
                {references.map((item) => (
                  <a
                    key={item.url}
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className={card + " flex items-center gap-3"}
                  >
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#327691]/10 text-[#327691]">
                      <LuExternalLink size={20} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <b className="block text-xs text-[#327691] dark:text-[#5FAFCB]">
                        {item.platform}
                      </b>
                      <span className="block truncate text-sm font-semibold">
                        {item.title || item.url.replace(/^https?:\/\//, "")}
                      </span>
                      {item.note && (
                        <span className="mt-1 block whitespace-pre-wrap break-words text-xs text-slate-500 dark:text-slate-400">
                          {item.note}
                        </span>
                      )}
                      {item.duration && (
                        <span className="text-xs text-slate-500">
                          {item.duration}
                        </span>
                      )}
                    </span>
                    <LuChevronRight className="shrink-0" />
                  </a>
                ))}
              </div>
            </section>
          )}
          <h3 className="mb-3 text-sm font-bold">Posted by</h3>
          <Link
            to={owner ? "/profile" : `/user-profile/${j.hostId}`}
            className={card + " flex items-center gap-3"}
          >
            <Avatar
              user={{
                ...poster.data,
                name: company,
                profilePic: poster.data?.profilePic || j.hostProfilePic,
              }}
              className="h-11 w-11 shrink-0 rounded-full object-cover"
            />
            <span className="min-w-0 flex-1">
              <b className="block truncate text-sm">{company}</b>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {postedCount === null
                  ? "View profile"
                  : postedCount +
                  (postedCount === 1 ? " job posted" : " jobs posted")}
              </span>
            </span>
            <LuChevronRight className="shrink-0 text-slate-400" />
          </Link>
          {open && (
            <button
              type="button"
              onClick={() => {
                setError("");
                setShareOpen(true);
              }}
              className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm font-bold text-[#327691] dark:text-[#5FAFCB]"
            >
              <LuSend size={16} />
              Not for you? Send it to a friend
            </button>
          )}
          <div className="my-5 flex items-start gap-2 rounded-xl bg-[#327691]/5 p-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
            <LuShieldCheck size={18} className="mt-0.5 shrink-0" />
            Applying is free, and it always will be. If a hirer asks you to send
            them money, report it. Keep the conversation in the app so your
            payment stays protected.
          </div>
          {notice && (
            <p
              role="status"
              className="mb-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200"
            >
              {notice}
            </p>
          )}
          {error && !applyOpen && !reportOpen && (
            <p
              role="alert"
              className="my-3 text-sm text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}
          <section
            className={
              card + " sticky bottom-0 z-10 mt-5 !rounded-[22px] shadow-lg"
            }
          >
            {!owner && !applied && open && <PayBreakdown amount={amount} />}
            {owner ? (
              <>
                <button
                  type="button"
                  className={primary}
                  onClick={() =>
                    applicantsRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                >
                  See who applied
                </button>
                <Link
                  to={`/post-job?jobId=${encodeURIComponent(jobId)}`}
                  className={secondary + " mt-3 w-full"}
                >
                  Edit job
                </Link>
                <p className={muted + " mt-2 text-center text-xs"}>
                  This is your job post.
                </p>
              </>
            ) : applied ? (
              <>
                <Link to="/my-applications" className={primary}>
                  <LuCheck size={18} />
                  Application sent
                </Link>
                <p className={muted + " mt-2 text-center text-xs"}>
                  You can manage it from My applications.
                </p>
                <Link to="/dashboard" className={secondary + " mt-3 w-full"}>
                  Back to Home
                </Link>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={startApply}
                  disabled={!open || apps.loading || Boolean(apps.error)}
                  className={primary}
                >
                  {apps.loading
                    ? "Checking application…"
                    : open
                      ? "Apply for this job"
                      : "This job is closed"}
                </button>
                <p className={muted + " mt-2 text-center text-xs"}>
                  {open
                    ? "Takes about a minute. You can withdraw any time."
                    : "This job is no longer accepting applications."}
                </p>
                {apps.error && (
                  <p role="alert" className="mt-2 text-xs text-red-600">
                    Could not check your application. Please reload before
                    applying.
                  </p>
                )}
              </>
            )}
          </section>
          {owner && (
            <section ref={applicantsRef} className="scroll-mt-5 pt-8">
              <h3 className="mb-4 text-lg font-bold">Applicants</h3>
              <State {...apps} empty={!rows(apps.data).length}>
                No applications yet.
              </State>
              <div className="space-y-4">
                {rows(apps.data).map((a) => {
                  const applicantId =
                    a.applicantId || a.uid || a.userId || a.id;
                  return (
                    <div key={a.id} className={card}>
                      <Link
                        to={`/user-profile/${applicantId}`}
                        className="flex items-center gap-3"
                      >
                        <Avatar
                          user={{
                            name: a.applicantName || a.name,
                            profilePic: a.applicantProfilePic,
                          }}
                        />
                        <span>
                          <b className="block">
                            {a.applicantName || a.name || "View applicant"}
                          </b>
                          <span className={muted}>{a.status || "applied"}</span>
                        </span>
                      </Link>
                      <p className="my-3 whitespace-pre-wrap break-words text-sm">
                        {a.coverMessage}
                      </p>
                      {safeUrl(a.portfolioLink) && (
                        <a
                          href={safeUrl(a.portfolioLink)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-bold text-[#327691]"
                        >
                          View portfolio
                        </a>
                      )}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Link to={`/chat/${applicantId}`} className={secondary}>
                          Message
                        </Link>
                        {["applied", "selected"].includes(a.status) && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => shortlist(a)}
                            className={secondary}
                          >
                            Shortlist
                          </button>
                        )}
                      </div>
                      {!j.hiredApplicantId &&
                        !["rejected", "withdrawn", "hired"].includes(
                          a.status,
                        ) && (
                          <div className="mt-4">
                            <HireCheckout job={j} jobId={jobId} applicant={a} />
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}
      <ModalSheet
        open={applyOpen && !applied}
        onClose={() => {
          if (!lock.current) setApplyOpen(false);
        }}
        title="Apply for this job"
      >
        <form onSubmit={apply}>
          <div className={card + " mb-5"}>
            <b className="text-sm">{j?.title}</b>
            <p className={muted}>
              {company} · {j?.location}
            </p>
          </div>
          <h3 className="mb-3 text-sm font-bold">You are applying as</h3>
          <div className={card + " flex items-center gap-3"}>
            <Avatar user={user.data || currentUser} />
            <span className="min-w-0 flex-1">
              <b className="block truncate text-sm">
                {user.data?.name || currentUser?.name}
              </b>
              <span className={muted}>
                {user.data?.niche || currentUser?.niche || "Creative"}
              </span>
            </span>
            <Link
              to="/edit-profile"
              className="text-sm font-bold text-[#327691] dark:text-[#5FAFCB]"
            >
              Edit
            </Link>
          </div>
          <p className="mb-5 mt-2 text-xs text-slate-500">
            Your profile and showreel are attached automatically.
          </p>
          <fieldset disabled={busy} className="min-w-0 space-y-5">
            <label className="block text-sm font-semibold">
              Portfolio link{" "}
              <span className="float-right text-xs font-normal text-slate-400">
                Optional
              </span>
              <span className="mt-1 block text-xs font-normal text-slate-500">
                Add a reel or case study that fits this brief.
              </span>
              <input
                type="url"
                maxLength={2048}
                value={portfolio}
                onChange={(e) => setPortfolio(e.target.value)}
                placeholder="https://yourportfolio.com"
                className={input}
              />
            </label>
            <label className="block text-sm font-semibold">
              Why you are a great fit *
              <span className="float-right text-xs font-normal text-slate-400">
                {pitch.length}/600
              </span>
              <span className="mt-1 block text-xs font-normal text-slate-500">
                Two or three sentences. Mention something specific from the
                brief.
              </span>
              <textarea
                value={pitch}
                onChange={(e) => setPitch(e.target.value.slice(0, 600))}
                maxLength={600}
                rows={5}
                required
                className={input}
                placeholder="Tell the hirer why you are right for this job."
              />
            </label>
          </fieldset>
          <p className="my-4 text-xs leading-5 text-slate-500 dark:text-slate-400">
            Applying is free. If hired, the 7% zylen fee comes off at payout and
            guarantees you get paid. Once the hirer approves the work, payout is
            within 2 business days.
          </p>
          <PayBreakdown amount={amount} />
          {error && (
            <p
              role="alert"
              className="my-3 text-sm text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy || !pitch.trim() || !linkOk || !open || applied}
            className={primary}
          >
            {busy && <LuLoaderCircle size={18} className="animate-spin" />}
            {busy ? "Sending…" : "Send application"}
          </button>
          <p className="mt-2 text-center text-xs text-slate-500">
            {company} sees your profile, showreel and this message.
          </p>
        </form>
      </ModalSheet>
      <ModalSheet
        open={bankOpen}
        onClose={() => setBankOpen(false)}
        title="Set up your bank account"
      >
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Connect a payout account before applying so you can receive payment.
        </p>
        <BankPayoutForm />
        <button
          type="button"
          disabled={!bankReady}
          className={primary + " mt-5"}
          onClick={() => {
            setBankOpen(false);
            setApplyOpen(true);
          }}
        >
          Continue to application
        </button>
        {!bankReady && (
          <p className="mt-2 text-center text-xs text-slate-500">
            Complete your bank setup above to continue.
          </p>
        )}
      </ModalSheet>
      <ShareJobSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        job={j}
        jobId={jobId}
      />
      <ModalSheet
        open={reportOpen}
        onClose={() => {
          if (!lock.current) setReportOpen(false);
        }}
        title="Report job post"
      >
        <form onSubmit={report}>
          <label className="block text-sm font-semibold">
            Reason
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              className={input}
            >
              {reasons.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          <label className="mt-4 block text-sm font-semibold">
            Note (optional)
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              rows={3}
              disabled={busy}
              className={input}
            />
          </label>
          {error && (
            <p role="alert" className="my-3 text-sm text-red-600">
              {error}
            </p>
          )}
          <button disabled={busy || !uid} className={primary + " mt-4"}>
            {busy ? "Sending…" : "Submit report"}
          </button>
        </form>
      </ModalSheet>
    </AppPage>
  );
}
